import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './api';
import type {
  CreateRunBody,
  RerunBody,
  RunProfileUpdate,
  TenantScheduleUpdate
} from '../../types/routing';

const ACTIVE_RUN_POLL_MS = 3000;
const RUNS_LIST_POLL_MS = 5000;

export const useRunsQuery = (filters: api.RunListFilters, autoRefresh: boolean) =>
  useQuery({
    queryKey: ['runs', filters],
    queryFn: () => api.listRuns(filters),
    refetchInterval: autoRefresh ? RUNS_LIST_POLL_MS : false
  });

export const useRunQuery = (runId: string) =>
  useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.getRun(runId),
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? ACTIVE_RUN_POLL_MS : false;
    }
  });

export const useRunRoutesQuery = (runId: string, runActive: boolean) =>
  useQuery({
    queryKey: ['run-routes', runId],
    queryFn: () => api.getRunRoutes(runId),
    refetchInterval: runActive ? ACTIVE_RUN_POLL_MS : false
  });

export const useRouteExplainQuery = (runId: string, routeId: string) =>
  useQuery({
    queryKey: ['route-explain', runId, routeId],
    queryFn: () => api.getRouteExplain(runId, routeId)
  });

// Geometry is computed on the server the first time it is asked for, so this is deliberately
// its own query: the routes payload stays free of polylines and only an opened route pays.
export const useRoutePathQuery = (
  runId: string,
  routeId: string,
  enabled: boolean,
  rideId?: number | null
) =>
  useQuery({
    // rideId is part of the key: a leg's geometry is its own answer, not a slice of the route's.
    queryKey: ['route-path', runId, routeId, rideId ?? 'whole'],
    queryFn: () => api.getRoutePath(runId, routeId, rideId),
    enabled,
    staleTime: Infinity
  });

export const useRouteMetricsQuery = (filters: api.RouteMetricsFilters) =>
  useQuery({
    queryKey: ['route-metrics', filters],
    queryFn: () => api.getRouteMetrics(filters)
  });

export const useCreateRunMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRunBody) => api.createRun(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['runs'] })
  });
};

/**
 * A rerun is a new run, so the listing has to pick it up; the source run is invalidated too
 * because nothing on it changes except that it now has a rerun to be compared against.
 */
export const useRerunMutation = (runId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RerunBody) => api.rerunRun(runId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] });
      queryClient.invalidateQueries({ queryKey: ['run', runId] });
    }
  });
};

/**
 * `to` is part of the key: comparing this run against another run is another question. Left at
 * the default stale time on purpose — a comparison opened while the rerun is still planning is a
 * partial diff, and pinning it would keep that partial answer for the life of the page.
 */
export const useRunComparisonQuery = (runId: string, to?: string | null) =>
  useQuery({
    queryKey: ['run-comparison', runId, to ?? 'its-source'],
    queryFn: () => api.getRunComparison(runId, to)
  });

export const useCancelRunMutation = (runId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.cancelRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['run', runId] });
      queryClient.invalidateQueries({ queryKey: ['runs'] });
    }
  });
};

export const useTenantsQuery = () =>
  useQuery({ queryKey: ['tenants'], queryFn: api.listTenants });

export const useTenantConfigQuery = (tenant: string) =>
  useQuery({
    queryKey: ['tenant-config', tenant],
    queryFn: () => api.getTenantConfig(tenant),
    enabled: !!tenant
  });

export const useTenantScheduleQuery = (tenant: string) =>
  useQuery({
    queryKey: ['tenant-schedule', tenant],
    queryFn: () => api.getTenantSchedule(tenant),
    enabled: !!tenant
  });

/**
 * Every fleet or company the tenant has, not the first page of them. The endpoints cap a page at
 * 500, and a picker that silently stops at 500 is one an operator cannot use to reach the rest.
 */
const allPages = async <T>(
  fetchPage: (offset: number) => Promise<{ total: number; offset: number; limit: number } & T>,
  itemsOf: (page: T) => unknown[]
) => {
  const first = await fetchPage(0);
  const items = [...itemsOf(first)];
  // The server decides the page size, so walk by what it actually returned rather than by what
  // was asked for — an infinite loop here would hang the screen, not just slow it.
  const step = Math.max(itemsOf(first).length, 1);
  for (let offset = step; items.length < first.total; offset += step) {
    const page = await fetchPage(offset);
    const batch = itemsOf(page);
    if (!batch.length) break;
    items.push(...batch);
  }
  return items;
};

export const useAllTenantFleetsQuery = (tenant: string) =>
  useQuery({
    queryKey: ['tenant-fleets-all', tenant],
    queryFn: () =>
      allPages(
        offset => api.listTenantFleets(tenant, { offset, limit: 500 }),
        page => page.fleets
      ) as Promise<api.FleetInRoutes[]>,
    enabled: !!tenant
  });

export const useAllTenantCompaniesQuery = (tenant: string) =>
  useQuery({
    queryKey: ['tenant-companies-all', tenant],
    queryFn: () =>
      allPages(
        offset => api.listTenantCompanies(tenant, { offset, limit: 500 }),
        page => page.companies
      ) as Promise<api.CompanyInRides[]>,
    enabled: !!tenant
  });

export const useSaveTenantScheduleMutation = (tenant: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TenantScheduleUpdate) => api.putTenantSchedule(tenant, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-schedule', tenant] })
  });
};

export const useRunProfilesQuery = (tenant: string) =>
  useQuery({
    queryKey: ['run-profiles', tenant],
    queryFn: () => api.listRunProfiles(tenant),
    enabled: !!tenant
  });

/**
 * The schedule is invalidated alongside the profiles: it carries the profile it plans inlined, so
 * a renamed or re-ordered profile would otherwise still read as its old self on the same screen.
 */
const useProfileMutation = <TVariables, TResult>(
  tenant: string,
  mutationFn: (variables: TVariables) => Promise<TResult>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['run-profiles', tenant] });
      queryClient.invalidateQueries({ queryKey: ['tenant-schedule', tenant] });
    }
  });
};

export const useCreateRunProfileMutation = (tenant: string) =>
  useProfileMutation(tenant, (body: RunProfileUpdate) => api.createRunProfile(tenant, body));

export const useUpdateRunProfileMutation = (tenant: string) =>
  useProfileMutation(tenant, ({ id, body }: { id: number; body: RunProfileUpdate }) =>
    api.updateRunProfile(tenant, id, body)
  );

export const useDeleteRunProfileMutation = (tenant: string) =>
  useProfileMutation(tenant, (id: number) => api.deleteRunProfile(tenant, id));

// Rides and routes page independently: they are two listings on two tabs, and one asking for
// its next page has no business re-reading the other.
export const useWorldRidesQuery = (tenant: string, page: api.WorldPageRequest) =>
  useQuery({
    queryKey: ['world-rides', tenant, page],
    queryFn: () => api.listWorldRides(tenant, page)
  });

export const useWorldRoutesQuery = (tenant: string, page: api.WorldPageRequest) =>
  useQuery({
    queryKey: ['world-routes', tenant, page],
    queryFn: () => api.listWorldRoutes(tenant, page)
  });

export const useRuntimeConfigQuery = () =>
  useQuery({ queryKey: ['runtime-config'], queryFn: api.getRuntimeConfig });

export const useHealthQuery = () =>
  useQuery({
    queryKey: ['health'],
    queryFn: api.getHealth,
    refetchInterval: 15000,
    retry: false
  });

// Each poll is a real call to the engine, so this runs far slower than the service health poll.
export const useEngineStatusQuery = (tenant: string) =>
  useQuery({
    queryKey: ['engine-status', tenant],
    queryFn: () => api.getEngineStatus(tenant),
    refetchInterval: 60000,
    retry: false
  });

export const useFleetsInRoutesQuery = (params: { fromDatetime: string; tenant: string }) =>
  useQuery({
    queryKey: ['fleets-in-routes', params],
    queryFn: () => api.getFleetsInRoutes(params),
    // Only the routes of a chosen day have fleets to offer.
    enabled: Boolean(params.fromDatetime)
  });

export const useTenantFleetsQuery = (tenant: string, page: api.PageRequest = {}) =>
  useQuery({
    queryKey: ['tenant-fleets', tenant, page],
    queryFn: () => api.listTenantFleets(tenant, page)
  });

export const useTenantDriversQuery = (tenant: string, page: api.PageRequest = {}) =>
  useQuery({
    queryKey: ['tenant-drivers', tenant, page],
    queryFn: () => api.listTenantDrivers(tenant, page)
  });

export const useCompaniesInRidesQuery = (params: { fromDatetime: string; tenant: string }) =>
  useQuery({
    queryKey: ['companies-in-rides', params],
    queryFn: () => api.getCompaniesInRides(params),
    // Only the rides of a chosen day have companies to offer.
    enabled: Boolean(params.fromDatetime)
  });

export const useTenantCompaniesQuery = (tenant: string, page: api.PageRequest = {}) =>
  useQuery({
    queryKey: ['tenant-companies', tenant, page],
    queryFn: () => api.listTenantCompanies(tenant, page)
  });

// The codes are shared by every tenant, but how many routes start from each is not.
export const useZipCodesQuery = (tenant: string, page: api.PageRequest = {}) =>
  useQuery({
    queryKey: ['zip-codes', tenant, page],
    queryFn: () => api.listZipCodes(tenant, page)
  });
